/**
 * MifeLife v2 — About Us (/hakkinda). TR / EN / DE.
 * Founder story, mission, values, vision, founder profile.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Heart, Lock, Zap, TrendingUp, Globe, ArrowRight, Linkedin } from 'lucide-react';
import { InfoShell } from '../../components/v2/V2InfoPrimitives';
import { SEO } from '../../components/SEO';

const COPY = {
  tr: {
    metaTitle: 'Hakkımızda — MifeLife | AI + İnsan Mentörlük Ekosistemi',
    metaDesc: 'MifeLife neden var? Kurucu Hakan Dalmış’ın hikayesi, misyonumuz, değerlerimiz ve vizyonumuz. AI yol haritanı çizer, gerçek mentörler hedefine ulaştırır.',
    eyebrow: 'HAKKIMIZDA',
    h1: 'İnsanların yolunu bulmasına yardım ediyoruz',
    lead: 'MifeLife, yapay zekânın rehberliğini gerçek insan mentörlüğüyle birleştiren hibrit bir gelişim ekosistemidir. Amacımız net: AI senin yol haritanı çizer, gerçek mentörler hedefine ulaşmana yardım eder.',
    whyTitle: 'MifeLife neden var?',
    why: [
      'Yıllarca insanların aynı duvarlara çarptığını gözlemledik: odak kaybı, yön belirsizliği, tükenmişlik, kariyer kararsızlığı ve karar felci. Çoğu insan eksik yetenekten değil, doğru rehberliğe ve net bir plana erişimsizlikten dolayı tıkanıyor.',
      'Kurucumuz Hakan Dalmış, bu tıkanmanın hem bireylerde hem de şirketlerde tekrarlandığını gördü. Geleneksel koçluk pahalı, erişimi zor ve çoğu zaman teoride kalıyordu. Yapay zekâ ise hızlı ama insani bağlamdan ve sorumluluktan yoksundu.',
      'MifeLife bu iki dünyayı birleştirmek için doğdu: AI’nın hız ve nesnelliğini, gerçek mentörlerin deneyimi, empatisi ve hesap verebilirliğiyle buluşturmak.',
    ],
    missionTitle: 'Misyonumuz',
    mission: 'AI senin yol haritanı çizer. Gerçek mentörler hedefine ulaşmana yardım eder.',
    valuesTitle: 'Temel değerlerimiz',
    values: [
      { icon: Heart, t: 'Önce insan', d: 'Teknoloji araçtır, insan amaçtır. Her kararı insanın gelişimi merkezinde alırız.' },
      { icon: Lock, t: 'Gizlilik', d: 'Verilerin sana aittir. Şeffaf, güvenli ve sömürüsüz bir veri yaklaşımı.' },
      { icon: Zap, t: 'Teoriden çok aksiyon', d: 'Bilgi değil, uygulanabilir adımlar. Her analiz somut bir sonraki adımla biter.' },
      { icon: TrendingUp, t: 'Sürekli gelişim', d: 'Tek seferlik tavsiye değil, ölçülebilir ve sürdürülebilir ilerleme.' },
      { icon: Globe, t: 'Küresel erişilebilirlik', d: 'Doğru mentöre coğrafyadan ve bütçeden bağımsız erişim.' },
    ],
    visionTitle: 'Vizyonumuz',
    vision: 'Dünyanın önde gelen hibrit AI + İnsan Mentörlük ekosistemi olmak — milyonlarca insanın ve şirketin doğru rehberlikle potansiyelini açığa çıkardığı bir dünya.',
    founderTitle: 'Kurucu',
    founderName: 'Hakan Dalmış',
    founderRole: 'Kurucu & CEO',
    founderBio: 'Yıllarını insanların ve ekiplerin gerçek gelişim engellerini anlamaya adadı. MifeLife’ı, yapay zekânın gücünü gerçek insan mentörlüğünün sıcaklığıyla birleştirerek herkesin erişebileceği bir rehberlik ekosistemine dönüştürmek için kurdu.',
    ctaTitle: 'Sen de yolculuğa başla',
    ctaSub: 'Ücretsiz AI analizini yap, en zayıf alanında uzman mentörle tanış.',
    ctaBtn: 'Ücretsiz analizi başlat',
    ctaMentor: 'Mentör olmak istiyorum',
  },
  en: {
    metaTitle: 'About Us — MifeLife | AI + Human Mentorship Ecosystem',
    metaDesc: 'Why MifeLife exists: founder Hakan Dalmış’s story, our mission, values and vision. AI draws your roadmap, real mentors get you to your goals.',
    eyebrow: 'ABOUT US',
    h1: 'We help people find their direction',
    lead: 'MifeLife is a hybrid growth ecosystem that combines AI guidance with real human mentorship. Our promise is simple: AI draws your roadmap, real mentors help you achieve it.',
    whyTitle: 'Why MifeLife exists',
    why: [
      'For years we watched people hit the same walls: loss of focus, lack of direction, burnout, career uncertainty and decision paralysis. Most people get stuck not from lack of talent, but from lack of access to the right guidance and a clear plan.',
      'Our founder, Hakan Dalmış, saw this stuckness repeat across both individuals and companies. Traditional coaching was expensive, hard to access and too often stayed theoretical. AI was fast but lacked human context and accountability.',
      'MifeLife was born to merge these two worlds: the speed and objectivity of AI with the experience, empathy and accountability of real mentors.',
    ],
    missionTitle: 'Our mission',
    mission: 'AI creates your roadmap. Real mentors help you achieve it.',
    valuesTitle: 'Our core values',
    values: [
      { icon: Heart, t: 'Human-first', d: 'Technology is the tool, the human is the purpose. Every decision centers human growth.' },
      { icon: Lock, t: 'Privacy', d: 'Your data belongs to you. A transparent, secure and non-exploitative data approach.' },
      { icon: Zap, t: 'Action over theory', d: 'Not information, but applicable steps. Every analysis ends with a concrete next move.' },
      { icon: TrendingUp, t: 'Continuous growth', d: 'Not one-off advice, but measurable and sustainable progress.' },
      { icon: Globe, t: 'Global accessibility', d: 'Access to the right mentor regardless of geography or budget.' },
    ],
    visionTitle: 'Our vision',
    vision: 'To become the world’s leading hybrid AI + Human Mentorship ecosystem — a world where millions of people and companies unlock their potential with the right guidance.',
    founderTitle: 'Founder',
    founderName: 'Hakan Dalmış',
    founderRole: 'Founder & CEO',
    founderBio: 'Spent years understanding the real growth obstacles of people and teams. Founded MifeLife to turn the power of AI and the warmth of real human mentorship into a guidance ecosystem accessible to everyone.',
    ctaTitle: 'Start your journey',
    ctaSub: 'Run your free AI analysis and meet an expert mentor in your weakest area.',
    ctaBtn: 'Start free analysis',
    ctaMentor: 'I want to become a mentor',
  },
  de: {
    metaTitle: 'Über uns — MifeLife | KI + Mensch Mentoring-Ökosystem',
    metaDesc: 'Warum es MifeLife gibt: die Geschichte von Gründer Hakan Dalmış, unsere Mission, Werte und Vision. KI zeichnet deine Roadmap, echte Mentoren bringen dich ans Ziel.',
    eyebrow: 'ÜBER UNS',
    h1: 'Wir helfen Menschen, ihre Richtung zu finden',
    lead: 'MifeLife ist ein hybrides Wachstums-Ökosystem, das KI-Anleitung mit echtem menschlichem Mentoring verbindet. Unser Versprechen: Die KI zeichnet deine Roadmap, echte Mentoren helfen dir, sie zu erreichen.',
    whyTitle: 'Warum es MifeLife gibt',
    why: [
      'Jahrelang sahen wir Menschen an dieselben Wände stoßen: Konzentrationsverlust, fehlende Richtung, Burnout, Karriereunsicherheit und Entscheidungslähmung. Die meisten bleiben nicht aus Talentmangel stecken, sondern weil ihnen Zugang zu der richtigen Anleitung und einem klaren Plan fehlt.',
      'Unser Gründer Hakan Dalmış erlebte diese Blockade bei Einzelpersonen wie bei Unternehmen. Klassisches Coaching war teuer, schwer zugänglich und blieb oft theoretisch. KI war schnell, aber ohne menschlichen Kontext und Verantwortung.',
      'MifeLife entstand, um diese beiden Welten zu verbinden: die Geschwindigkeit und Objektivität der KI mit der Erfahrung, Empathie und Verantwortung echter Mentoren.',
    ],
    missionTitle: 'Unsere Mission',
    mission: 'Die KI erstellt deine Roadmap. Echte Mentoren helfen dir, sie zu erreichen.',
    valuesTitle: 'Unsere Grundwerte',
    values: [
      { icon: Heart, t: 'Mensch zuerst', d: 'Technologie ist das Werkzeug, der Mensch der Zweck. Jede Entscheidung stellt das menschliche Wachstum in den Mittelpunkt.' },
      { icon: Lock, t: 'Datenschutz', d: 'Deine Daten gehören dir. Ein transparenter, sicherer und nicht ausbeuterischer Datenansatz.' },
      { icon: Zap, t: 'Handeln statt Theorie', d: 'Keine Informationen, sondern umsetzbare Schritte. Jede Analyse endet mit einem konkreten nächsten Schritt.' },
      { icon: TrendingUp, t: 'Kontinuierliches Wachstum', d: 'Keine einmalige Beratung, sondern messbarer und nachhaltiger Fortschritt.' },
      { icon: Globe, t: 'Globale Zugänglichkeit', d: 'Zugang zum richtigen Mentor – unabhängig von Geografie oder Budget.' },
    ],
    visionTitle: 'Unsere Vision',
    vision: 'Das weltweit führende hybride KI + Mensch Mentoring-Ökosystem zu werden – eine Welt, in der Millionen Menschen und Unternehmen mit der richtigen Anleitung ihr Potenzial entfalten.',
    founderTitle: 'Gründer',
    founderName: 'Hakan Dalmış',
    founderRole: 'Gründer & CEO',
    founderBio: 'Verbrachte Jahre damit, die echten Wachstumshindernisse von Menschen und Teams zu verstehen. Gründete MifeLife, um die Kraft der KI und die Wärme echten menschlichen Mentorings in ein für alle zugängliches Anleitungs-Ökosystem zu verwandeln.',
    ctaTitle: 'Beginne deine Reise',
    ctaSub: 'Mache deine kostenlose KI-Analyse und triff einen Experten-Mentor in deinem schwächsten Bereich.',
    ctaBtn: 'Kostenlose Analyse starten',
    ctaMentor: 'Ich möchte Mentor werden',
  },
};

export default function V2AboutPage() {
  const [lang, setLang] = useState('tr');
  const T = COPY[lang] || COPY.tr;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'MifeLife',
    url: 'https://mifelife.com',
    description: T.metaDesc,
    founder: { '@type': 'Person', name: 'Hakan Dalmış', jobTitle: 'Founder & CEO' },
  };

  return (
    <InfoShell lang={lang} onLang={setLang} testid="v2-about-page">
      <SEO title={T.metaTitle} description={T.metaDesc} path="/hakkinda" jsonLd={jsonLd} />
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        {/* Hero */}
        <div className="text-[11px] uppercase tracking-[0.18em] text-violet-300 mb-3">{T.eyebrow}</div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05] mb-5">{T.h1}</h1>
        <p className="text-base sm:text-lg text-slate-400 leading-relaxed max-w-3xl mb-16">{T.lead}</p>

        {/* Why */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold tracking-tight mb-5">{T.whyTitle}</h2>
          <div className="space-y-4 text-[15px] text-slate-300 leading-relaxed max-w-3xl">
            {T.why.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </section>

        {/* Mission */}
        <section className="mb-16">
          <div className="rounded-2xl border border-violet-400/25 bg-gradient-to-br from-violet-500/[0.08] to-cyan-500/[0.04] p-8 sm:p-10">
            <div className="text-[11px] uppercase tracking-[0.18em] text-violet-300 mb-3 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> {T.missionTitle}</div>
            <p className="text-2xl sm:text-3xl font-semibold tracking-tight leading-snug">{T.mission}</p>
          </div>
        </section>

        {/* Values */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold tracking-tight mb-6">{T.valuesTitle}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {T.values.map((v, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:border-white/20 transition" data-testid={`v2-about-value-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-violet-500/15 border border-violet-400/30 text-violet-300 flex items-center justify-center mb-3"><v.icon className="w-5 h-5" /></div>
                <h3 className="text-base font-semibold mb-1.5">{v.t}</h3>
                <p className="text-[13px] text-slate-400 leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Vision */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold tracking-tight mb-4">{T.visionTitle}</h2>
          <p className="text-[15px] text-slate-300 leading-relaxed max-w-3xl">{T.vision}</p>
        </section>

        {/* Founder */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold tracking-tight mb-6">{T.founderTitle}</h2>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-start" data-testid="v2-about-founder">
            <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-violet-500/40 to-cyan-500/30 border border-white/10 flex items-center justify-center shrink-0">
              <span className="text-3xl font-semibold text-white/90">HD</span>
            </div>
            <div>
              <div className="text-lg font-semibold">{T.founderName}</div>
              <div className="text-sm text-violet-300 mb-3">{T.founderRole}</div>
              <p className="text-[14px] text-slate-300 leading-relaxed max-w-xl">{T.founderBio}</p>
              <a href="#" className="inline-flex items-center gap-1.5 mt-4 text-xs text-slate-400 hover:text-white transition" data-testid="v2-about-founder-linkedin">
                <Linkedin className="w-3.5 h-3.5" /> LinkedIn
              </a>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 sm:p-10 text-center">
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mb-2">{T.ctaTitle}</h2>
          <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">{T.ctaSub}</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/analiz" className="inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 transition" data-testid="v2-about-cta-analiz">{T.ctaBtn} <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/mentor-basvuru" className="inline-flex items-center gap-2 h-12 px-6 rounded-lg border border-white/10 bg-white/[0.02] text-white text-sm font-semibold hover:bg-white/[0.05] transition" data-testid="v2-about-cta-mentor">{T.ctaMentor}</Link>
          </div>
        </section>
      </div>
    </InfoShell>
  );
}
